import React from 'react';
import type {
  MessageInfo,
  MessageInfoAttachment,
} from '../../../api/cmail/cmail.dto';
import {
  extractEmailAddresses,
  extractRecipientsFromReceivers,
  filterOutCurrentUser,
  removeDuplicateEmails,
  formatEmailList,
} from '../utils/emailAddressUtils';
import {
  createReplyContent,
  createForwardContent,
  extractMessageContent,
} from '../utils/htmlContentUtils';
import {
  cleanFormData,
  hasUnsavedChanges,
  createEmptyForm,
} from '../utils/formValidationUtils';
import { getSubjectWithPrefix } from '../utils/emailTypeUtils';

export interface FormState {
  to: string;
  cc: string;
  bcc: string;
  subject: string;
  body: string;
  attachments: MessageInfoAttachment[];
}

export interface UseEmailFormProps {
  message?: MessageInfo;
  mode?: 'new' | 'reply' | 'reply-single' | 'forward';
  userEmail?: string;
  user?: any;
  isInitializing: boolean;
  setIsInitializing: (value: boolean) => void;
}

export const useEmailForm = ({
  message,
  mode = 'new',
  userEmail,
  user,
  isInitializing,
  setIsInitializing,
}: UseEmailFormProps) => {
  const [form, setForm] = React.useState<FormState>({
    to: '',
    cc: '',
    bcc: '',
    subject: '',
    body: '',
    attachments: [],
  });

  const [originalForm, setOriginalForm] = React.useState<FormState>({
    to: '',
    cc: '',
    bcc: '',
    subject: '',
    body: '',
    attachments: [],
  });

  const isInitializedRef = React.useRef(false);
  const prevMessageIdRef = React.useRef<string | undefined>(undefined);
  const prevModeRef = React.useRef<string | undefined>(undefined);
  const prevUserEmailRef = React.useRef<string | undefined>(undefined);

  // Initialize form state based on message and mode
  React.useEffect(() => {
    // Only reset initialization flag when message or mode actually changes
    const msgId = message?.msgId;
    // Re-initialize when the message, mode, or userEmail changes. userEmail is
    // included because it can transition from a "loading..." placeholder to the
    // real address after the form first initializes; without re-initializing,
    // reply To/Cc filtering would stay based on the placeholder.
    if (
      msgId !== prevMessageIdRef.current ||
      mode !== prevModeRef.current ||
      userEmail !== prevUserEmailRef.current
    ) {
      isInitializedRef.current = false;
      prevMessageIdRef.current = msgId;
      prevModeRef.current = mode;
      prevUserEmailRef.current = userEmail;
    }

    // Handle case where message is not yet available
    if (!message && mode) {
      setIsInitializing(true);

      // For new emails without message, create empty form
      const emptyForm = createEmptyForm();
      setForm(emptyForm);

      // Only set originalForm on first initialization
      if (!isInitializedRef.current) {
        setOriginalForm(emptyForm);
        isInitializedRef.current = true;
      }

      setIsInitializing(false);
      return;
    }

    // Handle case where both message and mode are available
    if (message && mode) {
      setIsInitializing(true);

      try {
        let bodyContent = '';

        // Extract content from message using utility function with error handling
        const originalContent = extractMessageContent(message);

        // Check if this is a draft email (for editing existing drafts)
        const isDraft = (message as any)?.emailDirection === 'DRAFT';

        if (mode === 'reply' || mode === 'reply-single') {
          // Always create the standard reply content with quoted original message
          const replyContent = createReplyContent(
            originalContent,
            message.dateReceived,
            message.senderName,
            message.senderEmail
          );

          // If AI-generated content exists, prepend it to the reply
          if ((message as any).aiGeneratedBody) {
            // Convert newlines to <br> tags for proper HTML formatting
            const aiGeneratedHtml = (message as any).aiGeneratedBody.replace(
              /\n/g,
              '<br>'
            );
            bodyContent = `${aiGeneratedHtml}${replyContent}`;
          } else {
            bodyContent = replyContent;
          }
        } else if (mode === 'forward') {
          bodyContent = createForwardContent(
            originalContent,
            message.dateReceived,
            message.senderName,
            message.senderEmail,
            message.subject
          );
        } else if (isDraft) {
          // For draft emails, use the original content as-is
          bodyContent = originalContent || '<em>[No content available]</em>';
        }

        // Get current user email for filtering
        const currentUserEmail = userEmail?.toLowerCase();

        let replyTo = '';
        let replyCc = '';

        if (mode === 'reply') {
          // Try to get recipients from receivers array first, fall back to headers
          let originalTo: string[] = [];
          let originalCc: string[] = [];

          try {
            if (message.receivers && message.receivers.length > 0) {
              const recipients = extractRecipientsFromReceivers(
                message.receivers
              );
              originalTo = recipients.to;
              originalCc = recipients.cc;
            } else {
              // Fall back to headers format
              originalTo = extractEmailAddresses(
                message.headers?.To || message.headers?.to
              );
              originalCc = extractEmailAddresses(
                message.headers?.Cc ||
                  message.headers?.cc ||
                  message.headers?.CC
              );
            }
          } catch (error) {
            console.warn('Failed to extract recipients from message:', error);
            // Use fallback values
            originalTo = [];
            originalCc = [];
          }

          // The sender goes to "To" field
          replyTo = message.senderEmail || '';

          // All other recipients (excluding current user) go to "Cc" field
          const allOtherRecipients = removeDuplicateEmails(
            filterOutCurrentUser(
              [...originalTo, ...originalCc],
              currentUserEmail || ''
            ).filter((email) => email !== message.senderEmail?.toLowerCase())
          );

          replyCc = formatEmailList(allOtherRecipients);
        } else if (mode === 'reply-single') {
          // For reply-single, only reply to the sender
          replyTo = message.senderEmail || '';
          replyCc = '';
        }

        // Extract recipient information for drafts
        let draftTo = '';
        let draftCc = '';
        let draftBcc = '';
        if (isDraft) {
          try {
            if (message.receivers && message.receivers.length > 0) {
              const recipients = extractRecipientsFromReceivers(
                message.receivers
              );
              draftTo = formatEmailList(recipients.to);
              draftCc = formatEmailList(recipients.cc);
              draftBcc = formatEmailList(recipients.bcc);
            } else {
              // Fall back to headers format
              draftTo = formatEmailList(
                extractEmailAddresses(
                  message.headers?.To || message.headers?.to
                )
              );
              draftCc = formatEmailList(
                extractEmailAddresses(
                  message.headers?.Cc ||
                    message.headers?.cc ||
                    message.headers?.CC
                )
              );
              draftBcc = formatEmailList(
                extractEmailAddresses(
                  message.headers?.Bcc ||
                    message.headers?.bcc ||
                    message.headers?.BCC
                )
              );
            }
          } catch (error) {
            console.warn('Failed to extract draft recipients:', error);
            // Use fallback values for drafts
            draftTo = '';
            draftCc = '';
            draftBcc = '';
          }
        }

        const newForm: FormState = {
          to: (() => {
            if (mode === 'reply' || mode === 'reply-single') {
              return replyTo;
            }
            if (isDraft) {
              return draftTo;
            }
            return '';
          })(),
          cc: (() => {
            if (mode === 'reply' || mode === 'reply-single') {
              return replyCc;
            }
            if (isDraft) {
              return draftCc;
            }
            return '';
          })(),
          bcc: (() => {
            if (isDraft) {
              return draftBcc;
            }
            return '';
          })(),
          subject: getSubjectWithPrefix(mode, message.subject),
          body: bodyContent,
          attachments: (() => {
            if (mode === 'forward' || isDraft) {
              return message.attachments || [];
            }
            // For reply mode, include attachments ONLY if this is an AI-generated reply with quote attachments
            // CmailComposerModal passes a modified message where attachments contains ONLY the quote attachments (not original email attachments)
            if (
              (mode === 'reply' || mode === 'reply-single') &&
              (message as any).aiGeneratedBody
            ) {
              return message.attachments || [];
            }
            // For normal replies (without AI content), do NOT include attachments
            return [];
          })(),
        };

        // Only set form/originalForm on first initialization for this msgId/mode.
        // Guarding setForm prevents React Query background refetches (new message
        // object, same msgId) from clobbering user/AI edits.
        if (!isInitializedRef.current) {
          setForm(newForm);
          setOriginalForm(newForm);
          isInitializedRef.current = true;
        }
      } catch (error) {
        console.error('Failed to initialize email form:', error);

        // Fall back to an empty form only if we have not initialized yet. Do NOT
        // mark initialized here, so a later refetch with the same msgId can retry
        // initialization instead of staying stuck on the empty fallback form.
        if (!isInitializedRef.current) {
          const fallbackForm = createEmptyForm();
          setForm(fallbackForm);
          setOriginalForm(fallbackForm);
        }
      } finally {
        setIsInitializing(false);
      }
    } else {
      // Reset form for new email (when message is null)
      const emptyForm = createEmptyForm();

      setForm(emptyForm);
      // Only set originalForm on first initialization
      if (!isInitializedRef.current) {
        setOriginalForm(emptyForm);
        isInitializedRef.current = true;
      }

      setIsInitializing(false);
    }
  }, [message, mode, userEmail]);

  const handleChange =
    (key: keyof FormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm({ ...form, [key]: e.target.value });

  const handleBodyChange = (e: React.FormEvent<HTMLDivElement>) => {
    if (isInitializing) {
      return;
    } // Skip if we're still initializing

    const content = e.currentTarget.innerHTML;
    setForm((prev) => ({ ...prev, body: content }));
  };

  // Check if there are unsaved changes using utility function
  const hasUnsavedChangesCheck = React.useCallback(() => {
    return hasUnsavedChanges(form, originalForm, mode, message);
  }, [form, originalForm, mode, message]);

  return {
    form,
    setForm,
    originalForm,
    handleChange,
    handleBodyChange,
    cleanFormData,
    hasUnsavedChanges: hasUnsavedChangesCheck,
    isInitializedRef,
  };
};
